/* Snovera v2: progressive enhancement, no dependencies. */
(() => {
  'use strict';
  if (window.SnV2Motion) { window.SnV2Motion.init(document); return; }
  const reduced = window.matchMedia('(prefers-reduced-motion: reduce)');
  const instances = new Map();
  const clamp = (n, min, max) => Math.max(min, Math.min(max, n));
  const all = (scope, selector) => [
    ...(scope.matches?.(selector) ? [scope] : []), ...scope.querySelectorAll(selector)
  ].filter(el => el.closest('.sn-v2'));
  function component(el, name, setup) {
    let entries = instances.get(el);
    if (!entries) { entries = new Map(); instances.set(el, entries); }
    if (entries.has(name)) return;
    const controller = new AbortController();
    const cleanups = [];
    const ctx = {
      on(target, type, fn, options = {}) {
        target.addEventListener(type, fn, { ...options, signal: controller.signal });
      },
      cleanup(fn) { cleanups.push(fn); }
    };
    const entry = { destroy() { controller.abort(); cleanups.forEach(fn => fn()); } };
    entries.set(name, entry);
    try { Object.assign(entry, setup(el, ctx)); }
    catch (error) { entry.destroy(); entries.delete(name); console.warn('Snovera ' + name, error); }
  }
  function observeOnce(el, run, ctx, margin = '0px 0px -24px 0px') {
    if (!('IntersectionObserver' in window)) { run(); return; }
    const observer = new IntersectionObserver(entries => {
      if (entries.some(entry => entry.isIntersecting)) { observer.disconnect(); run(); }
    }, { rootMargin: margin, threshold: 0.01 });
    observer.observe(el);
    ctx.cleanup(() => observer.disconnect());
  }
  function reveal(el, ctx) {
    const show = () => { el.classList.remove('sn-reveal-pending'); el.classList.add('sn-is-visible'); };
    ctx.cleanup(() => { el.classList.remove('sn-reveal-pending'); el.style.removeProperty('--sn-reveal-delay'); });
    if (reduced.matches || !('IntersectionObserver' in window) || el.getBoundingClientRect().top < innerHeight) { show(); return; }
    const siblings = [...el.parentElement.children].filter(child => child.matches('[data-sn-reveal]'));
    el.style.setProperty('--sn-reveal-delay', Math.min(siblings.indexOf(el), 4) * 65 + 'ms');
    el.classList.add('sn-reveal-pending');
    observeOnce(el, show, ctx);
    ctx.on(reduced, 'change', event => { if (event.matches) show(); });
  }
  function counter(el, ctx) {
    const raw = el.dataset.snValue;
    if (raw == null || raw.trim() === '' || !Number.isFinite(Number(raw))) return;
    const value = Number(raw);
    const decimals = clamp((raw.split('.')[1] || '').length, 0, 4);
    const format = new Intl.NumberFormat(document.documentElement.lang || 'en', {
      minimumFractionDigits: decimals, maximumFractionDigits: decimals
    });
    const render = n => { el.textContent = (el.dataset.snPrefix || '') + format.format(n) + (el.dataset.snSuffix || ''); };
    let frame = 0;
    let started = false;
    const finish = () => { cancelAnimationFrame(frame); render(value); };
    observeOnce(el, () => {
      started = true;
      if (reduced.matches) { finish(); return; }
      const start = performance.now();
      function tick(now) {
        const progress = clamp((now - start) / 1200, 0, 1);
        render(value * (1 - Math.pow(1 - progress, 3)));
        if (progress < 1) frame = requestAnimationFrame(tick);
      }
      frame = requestAnimationFrame(tick);
    }, ctx);
    ctx.on(reduced, 'change', event => { if (event.matches && started) finish(); });
    ctx.cleanup(() => cancelAnimationFrame(frame));
  }
  function carousel(el, ctx) {
    const track = el.querySelector('.sn-carousel-track');
    if (!track) return;
    const slides = [...track.querySelectorAll('.sn-slide')];
    if (!slides.length) return;
    const previous = el.querySelector('[data-sn-prev]');
    const next = el.querySelector('[data-sn-next]');
    const dots = el.querySelector('[data-sn-dots]');
    const duration = Number(el.dataset.snAutoplay) || 0;
    let pages = [0], active = 0, frame = 0, timer = 0, drag = null;
    let hovered = false, focused = false, visible = true, paused = false, blockClickUntil = 0;
    let pauseButton = el.querySelector('[data-sn-autoplay-toggle]');
    const generatedPause = duration > 0 && !pauseButton;
    el.setAttribute('aria-roledescription', 'carousel');
    if (!el.hasAttribute('role')) el.setAttribute('role', 'region');
    if (!el.hasAttribute('aria-label') && !el.hasAttribute('aria-labelledby')) el.setAttribute('aria-label', el.dataset.snLabel || 'Content carousel');
    if (!track.hasAttribute('tabindex')) track.tabIndex = 0;
    slides.forEach((slide, index) => {
      slide.setAttribute('role', 'group');
      slide.setAttribute('aria-roledescription', 'slide');
      if (!slide.hasAttribute('aria-label')) slide.setAttribute('aria-label', (index + 1) + ' of ' + slides.length);
    });
    if (generatedPause) {
      pauseButton = document.createElement('button');
      pauseButton.type = 'button';
      pauseButton.className = 'sn-carousel-autoplay';
      pauseButton.setAttribute('data-sn-autoplay-toggle', '');
      (dots?.parentElement || el).append(pauseButton);
      ctx.cleanup(() => pauseButton.remove());
    }
    function schedule() {
      clearTimeout(timer);
      if (duration < 1 || pages.length < 2 || reduced.matches || document.hidden || hovered || focused || !visible || paused || drag) return;
      timer = setTimeout(() => go((active + 1) % pages.length), Math.max(2000, duration));
    }
    function update() {
      const scroll = Math.max(0, track.scrollLeft);
      active = pages.reduce((best, offset, index) => Math.abs(offset - scroll) < Math.abs(pages[best] - scroll) ? index : best, 0);
      if (previous) previous.disabled = active === 0;
      if (next) next.disabled = active === pages.length - 1;
      dots?.querySelectorAll('button[data-sn-page]').forEach((dot, index) => {
        dot.classList.toggle('is-active', index === active);
        dot.setAttribute('aria-current', index === active ? 'true' : 'false');
      });
      if (pauseButton) {
        pauseButton.hidden = pages.length < 2 || reduced.matches;
        pauseButton.textContent = paused ? 'Play carousel' : 'Pause carousel';
        pauseButton.setAttribute('aria-pressed', String(paused));
      }
    }
    function go(index) {
      index = clamp(index, 0, pages.length - 1);
      track.scrollTo({ left: pages[index], behavior: reduced.matches ? 'instant' : 'smooth' });
      schedule();
    }
    function measure() {
      const max = Math.max(0, track.scrollWidth - track.clientWidth);
      const left = track.getBoundingClientRect().left;
      const offsets = slides.map(slide => slide.getBoundingClientRect().left - left + track.scrollLeft);
      const first = offsets[0] || 0;
      const perPage = Math.max(1, slides.filter((slide, index) => offsets[index] - first + slide.getBoundingClientRect().width <= track.clientWidth + 2).length);
      const step = el.dataset.snStep === '1' ? 1 : perPage;
      const fresh = [0];
      for (let index = step; index < slides.length; index += step) {
        const offset = clamp(offsets[index] - first, 0, max);
        if (offset - fresh[fresh.length - 1] > 3) fresh.push(offset);
      }
      if (max - fresh[fresh.length - 1] > 3) fresh.push(max);
      if (fresh.length !== pages.length || !dots?.childElementCount) {
        if (dots) {
          const buttons = fresh.map((_, index) => {
            const button = document.createElement('button');
            button.type = 'button';
            button.dataset.snPage = String(index);
            button.className = 'sn-carousel-dot';
            button.setAttribute('aria-label', 'Go to slide group ' + (index + 1) + ' of ' + fresh.length);
            return button;
          });
          dots.replaceChildren(...buttons);
          dots.hidden = fresh.length < 2;
        }
      }
      pages = fresh;
      el.classList.toggle('sn-carousel-static', pages.length < 2);
      update(); schedule();
    }
    ctx.on(track, 'scroll', () => { cancelAnimationFrame(frame); frame = requestAnimationFrame(update); }, { passive: true });
    if (previous) ctx.on(previous, 'click', () => go(active - 1));
    if (next) ctx.on(next, 'click', () => go(active + 1));
    if (dots) ctx.on(dots, 'click', event => {
      const button = event.target.closest('[data-sn-page]');
      if (button && dots.contains(button)) go(Number(button.dataset.snPage));
    });
    ctx.on(el, 'keydown', event => {
      if (event.defaultPrevented || /INPUT|TEXTAREA|SELECT/.test(event.target.tagName) || event.target.isContentEditable) return;
      const target = event.key === 'ArrowLeft' ? active - 1 : event.key === 'ArrowRight' ? active + 1 : event.key === 'Home' ? 0 : event.key === 'End' ? pages.length - 1 : null;
      if (target !== null) { event.preventDefault(); go(target); }
    });
    ctx.on(track, 'pointerdown', event => {
      if (event.pointerType === 'touch' || event.button !== 0 || pages.length < 2 || event.target.closest('input,select,textarea,summary,video,[contenteditable="true"]')) return;
      drag = { id: event.pointerId, x: event.clientX, y: event.clientY, scroll: track.scrollLeft, page: active, moved: false };
      clearTimeout(timer);
    });
    ctx.on(window, 'pointermove', event => {
      if (!drag || event.pointerId !== drag.id) return;
      const distance = event.clientX - drag.x;
      if (!drag.moved && Math.abs(distance) < 6) return;
      if (!drag.moved && Math.abs(event.clientY - drag.y) > Math.abs(distance)) { drag = null; schedule(); return; }
      drag.moved = true;
      track.classList.add('is-dragging');
      if (!track.hasPointerCapture(event.pointerId)) track.setPointerCapture(event.pointerId);
      track.scrollLeft = drag.scroll - distance;
      event.preventDefault();
    }, { passive: false });
    function endDrag(event) {
      if (!drag || event.pointerId !== drag.id) return;
      const finished = drag;
      drag = null;
      track.classList.remove('is-dragging');
      if (track.hasPointerCapture(event.pointerId)) track.releasePointerCapture(event.pointerId);
      if (finished.moved) {
        blockClickUntil = performance.now() + 350;
        const distance = event.clientX - finished.x;
        go(Math.abs(distance) > Math.min(60, track.clientWidth * 0.15) ? finished.page + (distance < 0 ? 1 : -1) : active);
      } else schedule();
    }
    ctx.on(window, 'pointerup', endDrag);
    ctx.on(window, 'pointercancel', endDrag);
    ctx.on(track, 'dragstart', event => event.preventDefault());
    ctx.on(track, 'click', event => {
      if (performance.now() < blockClickUntil) { event.preventDefault(); event.stopPropagation(); }
    }, { capture: true });
    ctx.on(el, 'mouseenter', () => { hovered = true; schedule(); });
    ctx.on(el, 'mouseleave', () => { hovered = false; schedule(); });
    ctx.on(el, 'focusin', () => { focused = true; schedule(); });
    ctx.on(el, 'focusout', event => { focused = el.contains(event.relatedTarget); schedule(); });
    ctx.on(document, 'visibilitychange', schedule);
    ctx.on(reduced, 'change', () => { update(); schedule(); });
    if (pauseButton) ctx.on(pauseButton, 'click', () => { paused = !paused; update(); schedule(); });
    if ('IntersectionObserver' in window) {
      const observer = new IntersectionObserver(entries => { visible = entries[0].isIntersecting; schedule(); });
      observer.observe(el);
      ctx.cleanup(() => observer.disconnect());
    }
    if ('ResizeObserver' in window) {
      const observer = new ResizeObserver(measure);
      observer.observe(track);
      slides.forEach(slide => observer.observe(slide));
      ctx.cleanup(() => observer.disconnect());
    } else ctx.on(window, 'resize', measure, { passive: true });
    ctx.on(el, 'load', measure, { capture: true });
    ctx.cleanup(() => { clearTimeout(timer); cancelAnimationFrame(frame); track.classList.remove('is-dragging'); });
    measure();
  }
  function accordion(el, ctx) {
    const summary = el.querySelector('summary');
    const body = el.querySelector('.sn-accordion-body');
    if (!summary || !body) return;
    let animation = null, expanded = el.open;
    const restore = () => { body.style.removeProperty('overflow'); body.style.removeProperty('height'); };
    function set(open) {
      if (expanded === open && !animation) return;
      const start = el.open ? body.getBoundingClientRect().height : 0;
      animation?.cancel(); animation = null;
      expanded = open;
      summary.setAttribute('aria-expanded', String(open));
      if (open) {
        const group = el.closest('[data-sn-accordion-group]');
        group?.querySelectorAll('details[data-sn-accordion]').forEach(other => {
          if (other === el || other.closest('[data-sn-accordion-group]') !== group) return;
          const instance = instances.get(other)?.get('accordion');
          if (instance) instance.set(false); else other.open = false;
        });
      }
      el.open = true;
      restore();
      const end = open ? body.scrollHeight : 0;
      if (reduced.matches || !body.animate) { el.open = open; return; }
      body.style.overflow = 'hidden';
      animation = body.animate([{ height: start + 'px' }, { height: end + 'px' }], {
        duration: 280, easing: 'cubic-bezier(.2,.7,.2,1)'
      });
      animation.onfinish = () => { el.open = open; animation = null; restore(); };
    }
    summary.setAttribute('aria-expanded', String(expanded));
    ctx.on(summary, 'click', event => { event.preventDefault(); set(!expanded); });
    ctx.on(el, 'toggle', () => {
      if (!animation) { expanded = el.open; summary.setAttribute('aria-expanded', String(expanded)); }
    });
    ctx.on(reduced, 'change', event => {
      if (event.matches && animation) { animation.cancel(); animation = null; el.open = expanded; restore(); }
    });
    ctx.cleanup(() => { animation?.cancel(); el.open = expanded; restore(); });
    return { set };
  }
  function flips(el, ctx) {
    const cards = [...el.querySelectorAll('[data-sn-flip]')];
    function state(card, active) {
      card.classList.toggle('is-flipped', active);
      card.setAttribute('aria-pressed', String(active));
      card.querySelector('.sn-flip-front')?.setAttribute('aria-hidden', String(active));
      card.querySelector('.sn-flip-back')?.setAttribute('aria-hidden', String(!active));
    }
    cards.forEach(card => state(card, card.classList.contains('is-flipped')));
    ctx.on(el, 'click', event => {
      const selected = event.target.closest('[data-sn-flip]');
      if (!selected || !el.contains(selected)) return;
      const opening = !selected.classList.contains('is-flipped');
      cards.forEach(card => {
        const active = card === selected && opening;
        state(card, active);
      });
    });
  }
  function timeline(el, ctx) {
    const steps = [...el.querySelectorAll('.sn-timeline-step')];
    let frame = 0;
    function update() {
      if (!steps.length) return;
      const rects = steps.map(step => step.getBoundingClientRect());
      const trigger = innerHeight * 0.65;
      const start = rects[0].top + Math.min(rects[0].height / 2, 48);
      const last = rects[rects.length - 1];
      const end = last.top + Math.min(last.height / 2, 48);
      el.style.setProperty('--sn-timeline-progress', String(clamp((trigger - start) / Math.max(1, end - start), 0, 1)));
      steps.forEach((step, index) => step.classList.toggle('is-active', rects[index].top + Math.min(rects[index].height / 2, 48) <= trigger));
    }
    const schedule = () => { cancelAnimationFrame(frame); frame = requestAnimationFrame(update); };
    ctx.on(window, 'scroll', schedule, { passive: true });
    ctx.on(window, 'resize', schedule, { passive: true });
    if ('ResizeObserver' in window) {
      const observer = new ResizeObserver(schedule);
      observer.observe(el);
      ctx.cleanup(() => observer.disconnect());
    }
    ctx.cleanup(() => cancelAnimationFrame(frame));
    update();
  }
  let dialog, video, opener, oldOverflow;
  function closeVideo() { if (dialog?.open) dialog.close(); }
  function createVideoDialog() {
    if (dialog) return;
    dialog = document.createElement('dialog');
    dialog.className = 'sn-video-dialog';
    dialog.setAttribute('aria-label', 'Video player');
    const close = document.createElement('button');
    close.type = 'button'; close.className = 'sn-video-close'; close.textContent = '×';
    close.setAttribute('aria-label', 'Close video');
    video = document.createElement('video');
    video.controls = true; video.playsInline = true; video.preload = 'metadata';
    dialog.append(close, video);
    document.body.append(dialog);
    close.addEventListener('click', closeVideo);
    dialog.addEventListener('click', event => { if (event.target === dialog) closeVideo(); });
    dialog.addEventListener('close', () => {
      video.pause(); video.removeAttribute('src'); video.load();
      document.body.style.overflow = oldOverflow;
      if (opener?.isConnected) opener.focus({ preventScroll: true });
      opener = null;
    });
  }
  function videoTrigger(el, ctx) {
    ctx.on(el, 'click', event => {
      const source = el.dataset.snVideo?.trim();
      if (!source) return;
      let url;
      try { url = new URL(source, location.href); } catch (_) { return; }
      if (!['https:', 'http:'].includes(url.protocol)) return;
      createVideoDialog();
      if (!dialog.showModal) return;
      event.preventDefault();
      opener = el;
      oldOverflow = document.body.style.overflow;
      document.body.style.overflow = 'hidden';
      dialog.setAttribute('aria-label', el.dataset.snVideoTitle || 'Video player');
      video.src = url.href;
      dialog.showModal();
      video.play().catch(() => {});
    });
    ctx.cleanup(() => { if (opener === el) closeVideo(); });
  }
  // Snovera visit countdown v1: per-tab visit, with local date rollover.
  const snCountdownMemory = new Map();
  function resolveCountdown(options) {
    const { mode, fixedDeadline = '', now = Date.now, random = Math.random,
      sessionStore, localStore, memory = new Map(), key = 'snovera:visit-countdown:v1' } = options;
    if (mode === 'fixed') {
      const raw = String(fixedDeadline).trim();
      return /^\d{4}-\d{2}-\d{2}T/.test(raw) && /(?:Z|[+-]\d{2}:\d{2})$/i.test(raw) ? Date.parse(raw) : NaN;
    }
    const instant = Number(now());
    if (!Number.isFinite(instant)) return NaN;
    const date = new Date(instant);
    const day = [date.getFullYear(), date.getMonth() + 1, date.getDate()].join('-');
    const read = (storage, name) => {
      try { return storage?.getItem(name) ?? null; } catch (_) { return null; }
    };
    const write = (storage, name, value) => {
      try { storage?.setItem(name, value); } catch (_) { /* Continue with page memory. */ }
    };
    let record = memory.get(key);
    if (!record) {
      try { record = JSON.parse(read(sessionStore, key) || 'null'); } catch (_) { record = null; }
    }
    const valid = record && record.day === day && [2, 3, 4].includes(record.hours)
      && Number.isFinite(record.startedAt) && Number.isFinite(record.deadline)
      && record.deadline === record.startedAt + record.hours * 3600000;
    // Expiration deliberately does not invalidate a visit record.
    if (valid) { memory.set(key, record); return record.deadline; }
  
    const previousKey = key + ':last-hours';
    const storedPrevious = Number(read(localStore, previousKey));
    const previous = [2, 3, 4].includes(storedPrevious) ? storedPrevious
      : memory.get(previousKey) || record?.hours;
    const choices = [2, 3, 4].filter(hours => hours !== previous);
    const sample = Number(random());
    const unit = Number.isFinite(sample) ? Math.max(0, Math.min(0.9999999999999999, sample)) : 0.5;
    const hours = choices[Math.floor(unit * choices.length)];
    record = { day, hours, startedAt: instant, deadline: instant + hours * 3600000 };
    memory.set(key, record);
    memory.set(previousKey, hours);
    write(sessionStore, key, JSON.stringify(record));
    write(localStore, previousKey, String(hours));
    return record.deadline;
  }
  function countdown(el, ctx) {
    const fields = ['hours', 'minutes', 'seconds'].map(name => el.querySelector('[data-sn-' + name + '], [data-sn-countdown-part="' + name + '"]'));
    const mode = el.dataset.snCountdownMode === 'fixed' ? 'fixed' : 'visit';
    const getStorage = name => { try { return window[name]; } catch (_) { return null; } };
    const options = {
      mode, fixedDeadline: el.dataset.snDeadline || '', memory: snCountdownMemory,
      sessionStore: getStorage('sessionStorage'), localStore: getStorage('localStorage')
    };
    let timer = 0;
    function tick() {
      // Re-resolving also notices a local date change while this tab stays open.
      const deadline = resolveCountdown(options);
      if (!Number.isFinite(deadline)) {
        fields.forEach(field => { if (field) field.textContent = '--'; });
        el.classList.remove('is-expired');
        clearInterval(timer);
        return;
      }
      const seconds = Math.max(0, Math.ceil((deadline - Date.now()) / 1000));
      const values = [Math.floor(seconds / 3600), Math.floor(seconds / 60) % 60, seconds % 60];
      fields.forEach((field, index) => { if (field) field.textContent = String(values[index]).padStart(2, '0'); });
      el.classList.toggle('is-expired', seconds === 0);
      if (!seconds && mode === 'fixed') clearInterval(timer);
    }
    tick();
    const initial = resolveCountdown(options);
    if (Number.isFinite(initial) && (mode === 'visit' || initial > Date.now())) timer = setInterval(tick, 1000);
    ctx.on(document, 'visibilitychange', tick);
    ctx.cleanup(() => clearInterval(timer));
  }
  const features = [
    ['reveal', '[data-sn-reveal]', reveal], ['counter', '[data-sn-counter]', counter],
    ['carousel', '[data-sn-carousel]', carousel], ['accordion', 'details[data-sn-accordion]', accordion],
    ['flips', '[data-sn-flip-group]', flips], ['timeline', '[data-sn-timeline]', timeline],
    ['video', '[data-sn-video-open]', videoTrigger], ['countdown', '[data-sn-countdown]', countdown],
    ['marquee', '[data-sn-marquee]', el => {
      el.querySelectorAll('.sn-marquee-group').forEach((group, index) => {
        if (index > 0) { group.setAttribute('aria-hidden', 'true'); group.inert = true; }
      });
    }]
  ];
  function init(scope = document) {
    features.forEach(([name, selector, setup]) => all(scope, selector).forEach(el => component(el, name, setup)));
  }
  function destroy(scope) {
    instances.forEach((entries, el) => {
      if (scope === el || scope.contains(el)) { entries.forEach(entry => entry.destroy()); instances.delete(el); }
    });
  }
  window.SnV2Motion = { init, destroy };
  document.addEventListener('shopify:section:load', event => init(event.target));
  document.addEventListener('shopify:section:unload', event => destroy(event.target));
  document.addEventListener('shopify:block:select', event => {
    event.target.querySelectorAll('[data-sn-reveal]').forEach(el => el.classList.remove('sn-reveal-pending'));
    event.target.classList.remove('sn-reveal-pending');
  });
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', () => init(), { once: true });
  else init();
})();
